// Nasus Siphoning Strike — golden crescent axe descends from above and slams into
// the target with a sand-and-gold impact burst.

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";

const WINDUP_MS = 90;    // axe raised above target
const SWING_MS  = 210;   // axe sweeps downward
const IMPACT_MS = 220;   // golden burst + sand particles
const TOTAL = WINDUP_MS + SWING_MS + IMPACT_MS;

// Blade geometry
const BLADE_R   = 28;    // outer crescent radius
const INNER_R   = 17;    // inner crescent radius
const HANDLE_L  = 110;   // handle length

export class NasusAxeVFX implements Animation {
  private elapsed = 0;
  private impactFired = false;
  // Pre-compute approach angle once
  private approachAngle: number;

  constructor(
    private target: Point,
    private onImpact?: () => void,
  ) {
    // Axe always swings from upper-left/above toward the target
    this.approachAngle = Math.PI * 0.25; // 45° from the right (sweeping across)
  }

  update(dt: number): void {
    const prev = this.elapsed;
    this.elapsed = Math.min(this.elapsed + dt, TOTAL);
    const impactTime = WINDUP_MS + SWING_MS;
    if (!this.impactFired && prev < impactTime && this.elapsed >= impactTime) {
      this.impactFired = true;
      this.onImpact?.();
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const e = this.elapsed;
    if (e < WINDUP_MS) {
      this.drawWindup(ctx, e / WINDUP_MS);
    } else if (e < WINDUP_MS + SWING_MS) {
      this.drawSwing(ctx, (e - WINDUP_MS) / SWING_MS);
    } else {
      this.drawImpact(ctx, (e - WINDUP_MS - SWING_MS) / IMPACT_MS);
    }
  }

  private drawWindup(ctx: CanvasRenderingContext2D, t: number): void {
    // Axe held above — slight glow build-up indicating power charge
    const liftY = -80 - t * 10;    // raised above target
    const liftX = -28 + t * 8;     // slightly to the left
    const glowAlpha = 0.3 + t * 0.5;

    ctx.save();
    ctx.translate(this.target.x + liftX, this.target.y + liftY);
    ctx.rotate(-Math.PI * 0.45); // blade tilted back, ready to swing

    ctx.globalAlpha = glowAlpha;
    this.drawGoldAxe(ctx);

    // Power charge glow ring
    ctx.globalAlpha = t * 0.6;
    ctx.strokeStyle = "#ffdd44";
    ctx.lineWidth   = 2;
    ctx.shadowColor = "#ffaa00";
    ctx.shadowBlur  = 20 + t * 10;
    ctx.beginPath();
    ctx.arc(0, 0, BLADE_R * 0.6, 0, Math.PI * 2);
    ctx.stroke();

    ctx.restore();
  }

  private drawSwing(ctx: CanvasRenderingContext2D, t: number): void {
    const eased = Easing.easeInQuad(t);

    // Axe travels from raised position down to target
    const startX = this.target.x - 28 + 8;
    const startY = this.target.y - 90;
    const endX   = this.target.x;
    const endY   = this.target.y;

    const ax = startX + (endX - startX) * eased;
    const ay = startY + (endY - startY) * eased;

    // Rotation sweeps from tilted-back to striking forward
    const startAngle = -Math.PI * 0.45;
    const endAngle   =  Math.PI * 0.15;
    const rotation   = startAngle + (endAngle - startAngle) * eased;

    ctx.save();
    ctx.translate(ax, ay);
    ctx.rotate(rotation);

    // Whoosh trail
    if (t > 0.1) {
      const trailEased = Easing.easeInQuad(Math.max(0, t - 0.12));
      const tx = startX + (endX - startX) * trailEased - ax;
      const ty = startY + (endY - startY) * trailEased - ay;
      ctx.globalAlpha = (1 - eased) * 0.5;
      ctx.strokeStyle = "#ffcc44";
      ctx.lineWidth   = BLADE_R * 1.4;
      ctx.lineCap     = "round";
      ctx.shadowColor = "#ff8800";
      ctx.shadowBlur  = 16;
      ctx.beginPath();
      ctx.moveTo(tx, ty);
      ctx.lineTo(0, 0);
      ctx.stroke();
    }

    ctx.globalAlpha = 1;
    this.drawGoldAxe(ctx);

    // Sand/dust particles flying off blade during swing
    if (t > 0.3) {
      const pt = (t - 0.3) / 0.7;
      for (let i = 0; i < 5; i++) {
        const angle = (i / 5) * Math.PI + pt * Math.PI * 0.5;
        const dist  = 18 + i * 7 + pt * 20;
        const px    = Math.cos(angle) * dist;
        const py    = Math.sin(angle) * dist;
        ctx.globalAlpha = (1 - pt) * 0.6;
        ctx.fillStyle   = i % 2 === 0 ? "#d4a76a" : "#f0c860";
        ctx.shadowBlur  = 4;
        ctx.shadowColor = "#c8860a";
        ctx.beginPath();
        ctx.arc(px, py, 2.5 - i * 0.3, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.restore();
  }

  private drawImpact(ctx: CanvasRenderingContext2D, t: number): void {
    const fade = 1 - Easing.easeOutQuad(t);
    const x = this.target.x;
    const y = this.target.y;

    ctx.save();

    // Axe still visible at rest briefly
    if (t < 0.25) {
      const axeFade = 1 - (t / 0.25);
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(Math.PI * 0.15);
      ctx.globalAlpha = axeFade;
      this.drawGoldAxe(ctx);
      ctx.restore();
    }

    // Impact shockwave ring
    ctx.globalAlpha = fade * 0.9;
    ctx.strokeStyle = "#ffcc00";
    ctx.lineWidth   = 3;
    ctx.shadowColor = "#ff8800";
    ctx.shadowBlur  = 20;
    ctx.beginPath();
    ctx.arc(x, y, 6 + t * 42, 0, Math.PI * 2);
    ctx.stroke();

    // Second ring (delayed)
    if (t > 0.1) {
      const t2 = (t - 0.1) / 0.9;
      ctx.globalAlpha = (1 - t2) * 0.55;
      ctx.strokeStyle = "#ffd700";
      ctx.lineWidth   = 2;
      ctx.shadowBlur  = 14;
      ctx.beginPath();
      ctx.arc(x, y, 4 + t2 * 28, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Ground crack lines radiating outward
    const crackAngles = [0.1, 0.8, 1.5, 2.2, 2.9, 3.7, 4.5, 5.2];
    for (const a of crackAngles) {
      const len = 8 + t * 28;
      ctx.globalAlpha = fade * 0.7;
      ctx.strokeStyle = "#c8860a";
      ctx.lineWidth   = 1.5;
      ctx.shadowColor = "#ffcc44";
      ctx.shadowBlur  = 8;
      ctx.beginPath();
      ctx.moveTo(x + Math.cos(a) * 4,   y + Math.sin(a) * 4);
      ctx.lineTo(x + Math.cos(a) * len, y + Math.sin(a) * len);
      ctx.stroke();
    }

    // Sand/dust burst particles
    const SAND_COLORS = ["#d4a76a", "#f0c860", "#e8b840", "#c8860a", "#ffe08a"];
    const sandPts = [
      [0.6,  0.2], [1.4,  0.9], [0.3,  1.7], [-0.9, 1.5],
      [-1.6, 0.5], [-1.2, -1.0], [0.4, -1.4], [1.3, -0.7],
    ];
    for (let i = 0; i < sandPts.length; i++) {
      const [sx, sy] = sandPts[i];
      const dist = 8 + t * 26;
      const px   = x + sx * dist;
      const py   = y + sy * dist;
      ctx.globalAlpha = fade * 0.75;
      ctx.fillStyle   = SAND_COLORS[i % SAND_COLORS.length];
      ctx.shadowBlur  = 5;
      ctx.shadowColor = "#ffaa00";
      ctx.beginPath();
      ctx.arc(px, py, 3 - t * 1.5, 0, Math.PI * 2);
      ctx.fill();
    }

    // Central white flash
    if (t < 0.3) {
      const flashFade = 1 - (t / 0.3);
      const flashR = 10 * (1 - t * 2);
      if (flashR > 0) {
        ctx.globalAlpha = flashFade * 0.95;
        ctx.fillStyle   = "#ffffff";
        ctx.shadowBlur  = 0;
        ctx.beginPath();
        ctx.arc(x, y, flashR, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.restore();
  }

  // ─── Golden Egyptian crescent axe ─────────────────────────────────────────

  private drawGoldAxe(ctx: CanvasRenderingContext2D): void {
    // Handle
    ctx.shadowColor = "#8a6010";
    ctx.shadowBlur  = 6;
    const handleGrad = ctx.createLinearGradient(-HANDLE_L, -3, 0, 3);
    handleGrad.addColorStop(0, "#5a3c08");
    handleGrad.addColorStop(0.5, "#8a6010");
    handleGrad.addColorStop(1, "#4a3008");
    ctx.fillStyle = handleGrad;
    ctx.beginPath();
    ctx.roundRect(-HANDLE_L, -3, HANDLE_L + 4, 6, 2);
    ctx.fill();

    // Gold band around handle
    ctx.fillStyle   = "#c8960a";
    ctx.shadowBlur  = 4;
    ctx.shadowColor = "#ffcc44";
    for (const bx of [-HANDLE_L * 0.8, -HANDLE_L * 0.55, -HANDLE_L * 0.3]) {
      ctx.beginPath();
      ctx.rect(bx, -3.5, 5, 7);
      ctx.fill();
    }

    // Crescent blade body
    ctx.shadowColor = "#ff8800";
    ctx.shadowBlur  = 18;
    const bladeGrad = ctx.createRadialGradient(-4, -4, 2, 0, 0, BLADE_R);
    bladeGrad.addColorStop(0,   "#fff5aa");
    bladeGrad.addColorStop(0.35, "#ffd700");
    bladeGrad.addColorStop(0.7,  "#c8860a");
    bladeGrad.addColorStop(1,    "#7a4a05");
    ctx.fillStyle = bladeGrad;

    ctx.beginPath();
    // Outer crescent arc (leading edge)
    ctx.arc(0, 0, BLADE_R, -Math.PI * 0.65, Math.PI * 0.65, false);
    // Inner curve back (spine side)
    ctx.arc(BLADE_R * 0.55, 0, INNER_R, Math.PI * 0.68, -Math.PI * 0.68, true);
    ctx.closePath();
    ctx.fill();

    // Blade leading edge highlight
    ctx.globalAlpha *= 0.85;
    ctx.strokeStyle = "#fff5cc";
    ctx.lineWidth   = 2.5;
    ctx.shadowColor = "#ffee88";
    ctx.shadowBlur  = 12;
    ctx.beginPath();
    ctx.arc(0, 0, BLADE_R, -Math.PI * 0.65, Math.PI * 0.65, false);
    ctx.stroke();

    // Egyptian cartouche / hieroglyph line on blade
    ctx.globalAlpha *= 0.9;
    ctx.beginPath();
    ctx.moveTo(4, -10);
    ctx.quadraticCurveTo(14, 0, 4, 10);
    ctx.strokeStyle = "#c8960a";
    ctx.lineWidth   = 1;
    ctx.shadowColor = "#ffcc00";
    ctx.shadowBlur  = 5;
    ctx.stroke();

    // Two amber gems on the blade
    const gemPositions = [{ x: 10, y: -7 }, { x: 10, y: 7 }];
    for (const g of gemPositions) {
      ctx.shadowBlur  = 0;
      ctx.beginPath();
      ctx.ellipse(g.x, g.y, 3.5, 2, 0.2, 0, Math.PI * 2);
      ctx.fillStyle = "#2a1800";
      ctx.fill();

      ctx.shadowColor = "#ff8800";
      ctx.shadowBlur  = 10;
      ctx.beginPath();
      ctx.ellipse(g.x, g.y, 2.5, 1.4, 0.2, 0, Math.PI * 2);
      ctx.fillStyle = "#cc5500";
      ctx.fill();

      ctx.shadowBlur = 0;
      ctx.beginPath();
      ctx.ellipse(g.x - 0.6, g.y - 0.5, 1.2, 0.7, 0.2, 0, Math.PI * 2);
      ctx.fillStyle = "#ff9944";
      ctx.fill();
    }
  }

  isDone(): boolean {
    return this.elapsed >= TOTAL;
  }
}
